import * as React from "react"

import { cn } from "../../cn"

export interface ListTableProps extends React.TableHTMLAttributes<HTMLTableElement> {
  /**
   * Drops the table's own border, radius, and surface so an enclosing
   * container — a `Card`, most often — provides them and the row rules run
   * edge to edge. Pair with a container that removes its horizontal padding.
   */
  flush?: boolean
}

/**
 * A short, fully-rendered table for settings and detail surfaces: keys,
 * workspaces, endpoints. For large or sortable data sets, use `Table`, which
 * carries virtualization, sorting, and column controls.
 */
const ListTable = React.forwardRef<HTMLTableElement, ListTableProps>(
  ({ className, flush, ...props }, ref) => (
    <div
      className={cn(
        "w-full overflow-x-auto",
        !flush && "rounded-md border border-coval-border bg-surface-default",
      )}
    >
      <table
        ref={ref}
        className={cn("w-full border-collapse text-sm", className)}
        {...props}
      />
    </div>
  ),
)

ListTable.displayName = "ListTable"

const ListTableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "border-b border-coval-border bg-surface-contrast",
      className,
    )}
    {...props}
  />
))

ListTableHeader.displayName = "ListTableHeader"

const ListTableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={className} {...props} />
))

ListTableBody.displayName = "ListTableBody"

const ListTableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-coval-border transition-colors last:border-b-0 hover:bg-surface-0-hover",
      className,
    )}
    {...props}
  />
))

ListTableRow.displayName = "ListTableRow"

export interface ListTableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  /** Hides the label visually while keeping it for assistive tech. */
  srOnly?: boolean
}

const ListTableHead = React.forwardRef<
  HTMLTableCellElement,
  ListTableHeadProps
>(({ className, children, srOnly, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      "px-3 py-2 text-left text-xs font-medium text-coval-text-muted",
      className,
    )}
    {...props}
  >
    {srOnly ? <span className="sr-only">{children}</span> : children}
  </th>
))

ListTableHead.displayName = "ListTableHead"

const ListTableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn("px-3 py-2.5 align-middle text-coval-text", className)}
    {...props}
  />
))

ListTableCell.displayName = "ListTableCell"

export interface ListTablePrimaryCellProps extends Omit<
  React.TdHTMLAttributes<HTMLTableCellElement>,
  "title"
> {
  /** Secondary line under the label: a slug, description, or identifier. */
  meta?: React.ReactNode
  label: React.ReactNode
}

/** The row's identifying cell: a title with an optional second line. */
const ListTablePrimaryCell = React.forwardRef<
  HTMLTableCellElement,
  ListTablePrimaryCellProps
>(({ className, label, meta, ...props }, ref) => (
  <ListTableCell ref={ref} className={cn("min-w-0", className)} {...props}>
    <div className="min-w-0">
      <div className="truncate font-medium text-coval-text">{label}</div>
      {meta && (
        <div className="truncate text-xs text-coval-text-muted">{meta}</div>
      )}
    </div>
  </ListTableCell>
))

ListTablePrimaryCell.displayName = "ListTablePrimaryCell"

export interface ListTableEmptyProps {
  action?: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
  title: React.ReactNode
}

/** Placeholder shown in place of the table when there is nothing to list. */
function ListTableEmpty({
  action,
  description,
  icon,
  title,
}: ListTableEmptyProps) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-md border border-dashed border-coval-border px-6 py-10 text-center">
      {icon && <div className="text-coval-text-muted">{icon}</div>}
      <p className="text-sm font-medium text-coval-text">{title}</p>
      {description && (
        <p className="max-w-sm text-xs text-coval-text-muted">{description}</p>
      )}
      {action && <div className="pt-2">{action}</div>}
    </div>
  )
}

export {
  ListTable,
  ListTableBody,
  ListTableCell,
  ListTableEmpty,
  ListTableHead,
  ListTableHeader,
  ListTablePrimaryCell,
  ListTableRow,
}
